import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  TimePicker,
  Select,
  message,
  Table,
  Tag,
  Space,
  Modal,
  Descriptions,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

/**
 * 加班申请页面 - 供员工提交加班申请使用
 */
const OvertimeApplication = () => {
  const [form] = Form.useForm();
  const [applications, setApplications] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [stats, setStats] = useState({});
  const { user, token, isAuthenticated, isInitialized } = useAuthStore();

  /**
   * 获取项目列表（需登录后携带Token）
   */
  const fetchProjects = useCallback(async () => {
    if (!token) return; // 无token不请求，避免403
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/projects?hasUserTasks=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // 后端返回结构为 { success, data: { projects, pagination } }
        const list = data?.data?.projects || data?.projects || [];
        setProjects(list);
      } else {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || '获取项目列表失败');
      }
    } catch (error) {
      console.error('获取项目列表错误:', error);
    }
  }, [token]);

  /**
   * 获取任务列表（需登录后携带Token）
   */
  const fetchTasks = async (projectId) => {
    if (!projectId) {
      setTasks([]);
      return;
    }

    if (!token) return; // 无token不请求

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/tasks?projectId=${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // 兼容 { data: { tasks } } 或 { tasks }
        const list = data?.data?.tasks || data?.tasks || [];
        setTasks(list);
      } else {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || '获取任务列表失败');
      }
    } catch (error) {
      console.error('获取任务列表错误:', error);
    }
  };

  /**
   * 提交加班申请（需登录后携带Token）
   */
  const handleSubmit = async (values) => {
    if (!token) {
      message.warn('未登录或登录已过期，请重新登录');
      return;
    }
    setSubmitting(true);
    try {
      // 计算加班时长，仅用于前端基本校验
      const startTime = dayjs(values.startTime);
      const endTime = dayjs(values.endTime);
      const duration = endTime.diff(startTime, 'hour', true);

      if (duration <= 0) {
        message.error('结束时间必须晚于开始时间');
        return;
      }

      // 后端要求的请求体结构：
      // { taskId, projectId, overtimeSlots: [{ date, startTime, endTime, reason }] }
      const overtimeSlots = [{
        date: values.overtimeDate.format('YYYY-MM-DD'),
        startTime: values.startTime.format('HH:mm'),
        endTime: values.endTime.format('HH:mm'),
        reason: values.reason
      }];

      const requestData = {
        taskId: values.taskId,
        projectId: values.projectId,
        overtimeSlots
      };

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/overtime`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        message.success('加班申请提交成功');
        form.resetFields();
        setTasks([]);
        fetchMyApplications();
        fetchStats();
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.message || '提交失败');
      }
    } catch (error) {
      console.error('提交申请错误:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * 获取我的加班申请列表（需登录后携带Token）
   */
  const fetchMyApplications = useCallback(async () => {
    if (!token || !user?.id) return; // 无token或用户信息不请求
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      // 仅获取“待审批”的我的申请
      const response = await fetch(`${apiUrl}/overtime?userId=${user.id}&status=pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
  
      if (response.ok) {
        const data = await response.json();
        // 兼容 { data: { applications } } 或 { applications }
        const rawList = data?.data?.applications || data?.applications || [];
        // 扁平化映射：将后端返回的下划线字段与嵌套对象映射为前端表格所需字段
        const list = rawList.map(app => ({
          ...app,
          overtimeDate: app.overtimeDate || app.overtime_date,
          startTime: app.startTime || app.start_time,
          endTime: app.endTime || app.end_time,
          projectName: app.projectName || app.project?.name,
          taskTitle: app.taskTitle || app.task?.title,
          status: app.status || 'pending',
          createdAt: app.createdAt || app.created_at
        }));
        setApplications(list);
      } else {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || '获取申请列表失败');
      }
    } catch (error) {
      console.error('获取申请列表错误:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  /**
   * 获取统计信息（需登录后携带Token）
   */
  const fetchStats = useCallback(async () => {
    if (!token || !user?.id) return; // 无token或用户信息不请求
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/overtime/statistics?userId=${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // 兼容 { data: {...} } 或直接扁平
        setStats(data?.data || data || {});
      }
    } catch (error) {
      console.error('获取统计信息错误:', error);
    }
  }, [token, user?.id]);

  /**
   * 项目选择变化处理
   */
  const handleProjectChange = (projectId) => {
    form.setFieldsValue({ taskId: undefined });
    fetchTasks(projectId);
  };

  /**
   * 显示申请详情
   */
  const showDetail = (record) => {
    setSelectedApplication(record);
    setDetailVisible(true);
  };

  /**
   * 删除我的加班申请（仅允许删除“待审批”的记录）
   * - 调用后端 DELETE /overtime/:id 接口
   * - 成功后刷新我的申请列表
   */
  const handleDeleteApplication = async (record) => {
    if (record?.status !== 'pending') {
      message.warning('仅可删除待审批的申请');
      return;
    }
    if (!token) {
      message.warn('未登录或登录已过期，请重新登录');
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const res = await fetch(`${apiUrl}/overtime/${record.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await res.json().catch(() => ({}));
      if (res.ok) {
        message.success(result.message || '删除成功');
        fetchMyApplications();
      } else {
        message.error(result.message || '删除失败');
      }
    } catch (e) {
      console.error('删除申请错误:', e);
      message.error('网络错误，请稍后再试');
    }
  };

  /**
   * 修改我的加班申请（占位，未开通）
   * - 当前后端无更新接口，给出提示
   */
  const handleEditApplication = (record) => {
    message.info('暂不支持修改加班申请，如需调整请删除后重新提交');
  };

  /**
   * 获取状态标签
   */
  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待审批' },
      approved: { color: 'green', text: '已通过' },
      rejected: { color: 'red', text: '已拒绝' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 表格列定义
  const columns = [
    {
      title: '加班日期',
      dataIndex: 'overtimeDate',
      key: 'overtimeDate',
      render: (date) => (
        <Space>
          <CalendarOutlined />
          {dayjs(date).format('YYYY-MM-DD')}
        </Space>
      )
    },
    {
      title: '时间段',
      key: 'timeRange',
      render: (_, record) => (
        <Space>
          <ClockCircleOutlined />
          {record.startTime} - {record.endTime}
        </Space>
      )
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => `${duration}小时`
    },
    {
      title: '项目',
      dataIndex: 'projectName',
      key: 'projectName'
    },
    {
      title: '任务',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showDetail(record)}
          >
            详情
          </Button>
          {record.status === 'pending' && (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditApplication(record)}
              >
                修改
              </Button>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteApplication(record)}
              >
                删除
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  /**
   * 页面初始化后拉取数据：等待认证初始化完成，并且已登录且有token
   */
  useEffect(() => {
    if (!isInitialized) return; // 等待初始化完成
    if (!isAuthenticated || !token || !user?.id) return; // 未登录不请求
    fetchProjects();
    fetchMyApplications();
    fetchStats();
  }, [isInitialized, isAuthenticated, token, user?.id, fetchProjects, fetchMyApplications, fetchStats]);

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计卡片（按业务要求：隐藏该区域） */}
      {false && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总申请数"
                value={stats.totalApplications || 0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="待审批"
                value={stats.pendingApplications || 0}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="已通过"
                value={stats.approvedApplications || 0}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总加班时长"
                value={stats.totalHours || 0}
                suffix="小时"
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 申请表单 */}
      <Card title="提交加班申请" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            // 默认开始时间为 08:30，结束时间为 17:30（使用 dayjs 对应 TimePicker 的值类型）
            startTime: dayjs('08:30', 'HH:mm'),
            endTime: dayjs('17:30', 'HH:mm'),
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="projectId"
                label="项目"
                rules={[{ required: true, message: '请选择项目' }]}
              >
                <Select
                  placeholder="请选择项目"
                  onChange={handleProjectChange}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {projects.map(project => (
                    <Option key={project.id} value={project.id}>
                      {project.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="taskId"
                label="任务"
                rules={[{ required: true, message: '请选择任务' }]}
              >
                <Select
                  placeholder="请先选择项目"
                  disabled={tasks.length === 0}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {tasks.map(task => (
                    <Option key={task.id} value={task.id}>
                      {task.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="overtimeDate"
                label="加班日期"
                rules={[{ required: true, message: '请选择加班日期' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="选择日期"
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="startTime"
                label="开始时间"
                rules={[{ required: true, message: '请选择开始时间' }]}
              >
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  placeholder="开始时间"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="endTime"
                label="结束时间"
                rules={[{ required: true, message: '请选择结束时间' }]}
              >
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  placeholder="结束时间"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="reason"
            label="加班原因"
            rules={[{ required: true, message: '请填写加班原因' }]}
          >
            <TextArea
              rows={4}
              placeholder="请详细说明加班原因..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<PlusOutlined />}
            >
              提交申请
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 我的申请列表 */}
      <Card title="我的加班申请">
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={applications}
            loading={loading}
            rowKey="id"
            size="middle"
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
          />
        </div>
        {/* 操作图例说明 */}
        <div style={{ marginTop: 8, color: '#888' }}>
          <Space>
            <span>操作图例：</span>
            <Space size="small">
              <EditOutlined /> 修改
              <DeleteOutlined style={{ color: '#ff4d4f' }} /> 删除
            </Space>
          </Space>
        </div>
      </Card>

      {/* 详情对话框 */}
      <Modal
        title="加班申请详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {selectedApplication && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="加班日期">
              {dayjs(selectedApplication.overtimeDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="时间段">
              {selectedApplication.startTime} - {selectedApplication.endTime}
            </Descriptions.Item>
            <Descriptions.Item label="加班时长">
              {selectedApplication.duration}小时
            </Descriptions.Item>
            <Descriptions.Item label="项目">
              {selectedApplication.projectName}
            </Descriptions.Item>
            <Descriptions.Item label="任务" span={2}>
              {selectedApplication.taskTitle}
            </Descriptions.Item>
            <Descriptions.Item label="加班原因" span={2}>
              {selectedApplication.reason}
            </Descriptions.Item>
            <Descriptions.Item label="申请状态">
              {getStatusTag(selectedApplication.status)}
            </Descriptions.Item>
            <Descriptions.Item label="申请时间">
              {dayjs(selectedApplication.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            {selectedApplication.approvedBy && (
              <>
                <Descriptions.Item label="审批人">
                  {selectedApplication.approverName}
                </Descriptions.Item>
                <Descriptions.Item label="审批时间">
                  {dayjs(selectedApplication.approvedAt).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                {selectedApplication.approvalComment && (
                  <Descriptions.Item label="审批意见" span={2}>
                    {selectedApplication.approvalComment}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default OvertimeApplication;